import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, Gift, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { loyaltyApi } from '@/services/api';
import { formatMoney } from '@/utils/currency';
import { getApiErrorMessage } from '@/utils/apiError';

type Tab = 'liability' | 'activity' | 'members' | 'adjustments';

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function daysAgoIso(n: number) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

export default function LoyaltyReportsSection() {
  const [tab, setTab] = useState<Tab>('liability');
  const [fromDate, setFromDate] = useState(daysAgoIso(30));
  const [toDate, setToDate] = useState(todayIso());
  const [csvBusy, setCsvBusy] = useState(false);

  const liabilityQ = useQuery({
    queryKey: ['loyalty-liability'],
    queryFn: async () => (await loyaltyApi.liabilityReport()).data.message as {
      by_type?: { customer_type?: string; members?: number; points?: number; value?: number }[];
      total_points?: number;
      total_value?: number;
      due_to_expire?: Record<string, number>;
    },
    enabled: tab === 'liability',
  });

  const activityQ = useQuery({
    queryKey: ['loyalty-activity', fromDate, toDate],
    queryFn: async () =>
      (
        await loyaltyApi.activityReport({ from_date: fromDate, to_date: toDate })
      ).data.message as {
        by_type?: { transaction_type?: string; cnt?: number; points?: number }[];
        redemption_rate_pct?: number;
      },
    enabled: tab === 'activity',
  });

  const membersQ = useQuery({
    queryKey: ['loyalty-members'],
    queryFn: async () =>
      (await loyaltyApi.memberReport()).data.message as {
        members?: number;
        active_30d?: number;
        consent_opt_in?: number;
        consent_rate_pct?: number;
      },
    enabled: tab === 'members',
  });

  const adjustmentsQ = useQuery({
    queryKey: ['loyalty-adjustments'],
    queryFn: async () =>
      (await loyaltyApi.adjustmentsAudit(false)).data.message as {
        adjustments?: Record<string, unknown>[];
      },
    enabled: tab === 'adjustments',
  });

  const tabs = useMemo(
    () =>
      [
        { id: 'liability' as const, label: 'Liability' },
        { id: 'activity' as const, label: 'Activity' },
        { id: 'members' as const, label: 'Members' },
        { id: 'adjustments' as const, label: 'Adjustments' },
      ] as const,
    [],
  );

  const downloadCsv = async () => {
    setCsvBusy(true);
    try {
      const res = await loyaltyApi.adjustmentsAudit(true);
      const blob = res.data instanceof Blob ? res.data : new Blob([String(res.data)]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'loyalty_adjustments.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'CSV download failed'));
    } finally {
      setCsvBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-3 py-1.5 text-sm rounded-lg border ${
              tab === t.id
                ? 'bg-primary-50 border-primary-300 text-primary-800 font-semibold'
                : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'activity' && (
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="text-xs font-medium text-gray-600">From</label>
            <input
              type="date"
              className="input-field"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600">To</label>
            <input
              type="date"
              className="input-field"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>
        </div>
      )}

      {tab === 'liability' && (
        <div className="card p-5 space-y-4">
          <div className="flex items-center gap-2 text-emerald-800">
            <Gift className="w-5 h-5" />
            <h2 className="font-semibold">Outstanding liability</h2>
          </div>
          {liabilityQ.isLoading ? (
            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
          ) : liabilityQ.isError ? (
            <p className="text-sm text-red-600">{getApiErrorMessage(liabilityQ.error, 'Failed to load')}</p>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <Stat label="Points outstanding" value={String(Math.floor(liabilityQ.data?.total_points || 0))} />
                <Stat label="Liability value" value={formatMoney(Number(liabilityQ.data?.total_value) || 0)} />
                <Stat
                  label="Expire ≤30d"
                  value={String(Math.floor(Number(liabilityQ.data?.due_to_expire?.['30']) || 0))}
                />
                <Stat
                  label="Expire ≤60/90"
                  value={`${Math.floor(Number(liabilityQ.data?.due_to_expire?.['60']) || 0)} / ${Math.floor(Number(liabilityQ.data?.due_to_expire?.['90']) || 0)}`}
                />
              </div>
              {(liabilityQ.data?.by_type || []).length > 0 && (
                <RowsTable
                  rows={(liabilityQ.data?.by_type || []) as Record<string, unknown>[]}
                  columns={['customer_type', 'members', 'points', 'value']}
                />
              )}
            </>
          )}
        </div>
      )}

      {tab === 'activity' && (
        <div className="card p-5 space-y-4">
          <h2 className="font-semibold text-gray-900">Activity</h2>
          {activityQ.isLoading ? (
            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
          ) : activityQ.isError ? (
            <p className="text-sm text-red-600">{getApiErrorMessage(activityQ.error, 'Failed to load')}</p>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <Stat
                  label="Redemption rate"
                  value={`${Number(activityQ.data?.redemption_rate_pct || 0).toFixed(1)}%`}
                />
              </div>
              <RowsTable
                rows={(activityQ.data?.by_type || []) as Record<string, unknown>[]}
                columns={['transaction_type', 'cnt', 'points']}
              />
            </>
          )}
        </div>
      )}

      {tab === 'members' && (
        <div className="card p-5 space-y-4">
          <h2 className="font-semibold text-gray-900">Members</h2>
          {membersQ.isLoading ? (
            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
          ) : membersQ.isError ? (
            <p className="text-sm text-red-600">{getApiErrorMessage(membersQ.error, 'Failed to load')}</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <Stat label="Enrolled" value={String(membersQ.data?.members ?? 0)} />
              <Stat label="Active (30d)" value={String(membersQ.data?.active_30d ?? 0)} />
              <Stat label="Marketing consent" value={String(membersQ.data?.consent_opt_in ?? 0)} />
              <Stat
                label="Consent rate"
                value={`${Number(membersQ.data?.consent_rate_pct || 0).toFixed(1)}%`}
              />
            </div>
          )}
        </div>
      )}

      {tab === 'adjustments' && (
        <div className="card p-5 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <h2 className="font-semibold text-gray-900">Adjustments audit</h2>
            <button
              type="button"
              className="btn-secondary text-sm inline-flex items-center gap-1.5"
              disabled={csvBusy}
              onClick={() => void downloadCsv()}
            >
              {csvBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              CSV
            </button>
          </div>
          {adjustmentsQ.isLoading ? (
            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
          ) : adjustmentsQ.isError ? (
            <p className="text-sm text-red-600">{getApiErrorMessage(adjustmentsQ.error, 'Failed to load')}</p>
          ) : (
            <RowsTable
              rows={adjustmentsQ.data?.adjustments || []}
              columns={['creation', 'pos_customer', 'points', 'reason', 'adjusted_by', 'remarks']}
            />
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2.5">
      <p className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">{label}</p>
      <p className="text-lg font-semibold text-gray-900 tabular-nums mt-0.5">{value}</p>
    </div>
  );
}

function RowsTable({ rows, columns }: { rows: Record<string, unknown>[]; columns: string[] }) {
  if (!rows?.length) {
    return <p className="text-sm text-gray-500">No rows for this period.</p>;
  }
  return (
    <div className="overflow-x-auto border border-gray-100 rounded-lg">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
          <tr>
            {columns.map((c) => (
              <th key={c} className="px-3 py-2 font-semibold">
                {c.replace(/_/g, ' ')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-gray-50">
              {columns.map((c) => (
                <td key={c} className="px-3 py-2 text-gray-800 whitespace-nowrap">
                  {r[c] == null ? '—' : String(r[c])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
